import { useEffect, useRef, useState } from 'react'
import TitleHeader from '../TitleHeader/TitleHeader'
import arrowicon from '../../../assets/images/icons/arrowicon.svg'
import Constants from '../../../app/utils/constants'

const borderClasses = [
  'bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]',
  'bg-[linear-gradient(138.94deg,#EA892A_0%,rgba(237,110,94,0)_50%,#F0578B_100%)]',
]

const fillBase = [
  'before:bg-[linear-gradient(180deg,#0E0924_30%,#170f3a_70%,#231555_100%)]',
  'before:bg-[linear-gradient(180deg,#0E0924_20%,#0e0924d1_50%,#3a1520ca_100%)]',
]

const fillHover = [
  'after:bg-[linear-gradient(180deg,#0e0924ea_30%,#6932ffa2_80%,#6932ffcc_100%)]',
  'after:bg-[linear-gradient(180deg,#0e0924e2_20%,#f0697088_80%,#f0575fdd_100%)]',
]

const serviceLinks: Record<string, string> = {
  React: 'https://react.dev',
  AngularJS: 'https://angularjs.org',
  'React Native': 'https://reactnative.dev',
  'Next.Js': 'https://nextjs.org',
  Swift: 'https://swift.org',
  Java: 'https://java.com',
  Kotlin: 'https://kotlinlang.org',
  Flutter: 'https://flutter.dev',
  'Vue.Js': 'https://vuejs.org',
}

const COLS = 3
const MOBILE_COLS = 2

const FrontendService = () => {
  const { frontendServices: services } = Constants

  // ── Cursor glow ──────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  useEffect(() => {
    let clientX = 0
    let clientY = 0
    let currentX = 0
    let currentY = 0
    let rafId = 0
    let inside = false

    const container = containerRef.current
    if (!container) return

    const getRelativePos = () => {
      const rect = container.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    }

    const setPos = (x: number, y: number) => {
      if (blobRef.current)
        blobRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
    }

    const handleMouseMove = (e: MouseEvent) => { clientX = e.clientX; clientY = e.clientY }

    const handleEnter = (e: MouseEvent) => {
      clientX = e.clientX; clientY = e.clientY
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
      inside = true; setIsInside(true)
    }

    const handleLeave = () => { inside = false; setIsInside(false) }

    const handleScroll = () => {
      if (!inside) return
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
    }

    const animate = () => {
      if (inside) {
        const { x, y } = getRelativePos()
        currentX += (x - currentX) * 0.1
        currentY += (y - currentY) * 0.1
        setPos(currentX, currentY)
      }
      rafId = requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafId = requestAnimationFrame(animate)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])
  // ─────────────────────────────────────────────────────────────────────────

  // Desktop rows: groups of 3
  const desktopRows: typeof services[] = []
  for (let i = 0; i < services.length; i += COLS) desktopRows.push(services.slice(i, i + COLS))

  // Mobile rows: groups of 2
  const mobileRows: typeof services[] = []
  for (let i = 0; i < services.length; i += MOBILE_COLS) mobileRows.push(services.slice(i, i + MOBILE_COLS))

  // Separate visible state for desktop and mobile rows
  const [visibleDesktopRows, setVisibleDesktopRows] = useState<boolean[]>(
    () => new Array(desktopRows.length).fill(false)
  )
  const [visibleMobileRows, setVisibleMobileRows] = useState<boolean[]>(
    () => new Array(mobileRows.length).fill(false)
  )

  const desktopRowRefs = useRef<(HTMLDivElement | null)[]>([])
  const mobileRowRefs = useRef<(HTMLDivElement | null)[]>([])

  // Observer for desktop rows
  useEffect(() => {
    const observers: IntersectionObserver[] = []
    desktopRows.forEach((_, rowIndex) => {
      const el = desktopRowRefs.current[rowIndex]
      if (!el) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisibleDesktopRows((prev) => {
            const next = [...prev]
            next[rowIndex] = entry.isIntersecting
            return next
          })
        },
        { threshold: 0.25 },
      )
      observer.observe(el)
      observers.push(observer)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [desktopRows.length])

  // Observer for mobile rows
  useEffect(() => {
    const observers: IntersectionObserver[] = []
    mobileRows.forEach((_, rowIndex) => {
      const el = mobileRowRefs.current[rowIndex]
      if (!el) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisibleMobileRows((prev) => {
            const next = [...prev]
            next[rowIndex] = entry.isIntersecting
            return next
          })
        },
        { threshold: 0.25 },
      )
      observer.observe(el)
      observers.push(observer)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [mobileRows.length])

  const renderCard = (
    service: (typeof services)[0],
    globalIndex: number,
    colIndex: number,
    isVisible: boolean,
    hiddenClass: string,
    totalInRow: number,
  ) => {
    const isLoneItem = totalInRow === 1

    return (
      <div
      key={service.title}
      className={`w-full max-w-[150px] rounded-full p-[1.5px]
        xs:max-w-[165px]
        sm:min-w-38 sm:max-w-none
        md:min-w-46
        lg:min-w-46
        xl:w-full xl:max-w-60
        2xl:max-w-[20rem]
        ${isLoneItem ? 'col-span-2 mx-auto' : ''}
        ${borderClasses[globalIndex % 2]}
        ${isVisible ? 'circle-visible' : hiddenClass}`}
        style={{
          transitionDelay: isVisible ? `${colIndex * 0.12}s` : `${(2 - colIndex) * 0.08}s`,
        }}
      >
        <button
          type="button"
          onClick={() => window.open(serviceLinks[service.title], '_blank', 'noopener,noreferrer')}
          className={`group relative flex w-full aspect-square min-h-[150px]
            items-center justify-center overflow-hidden rounded-full px-2 py-2 text-center sm:px-3 md:px-5
            before:absolute before:inset-0 before:rounded-full before:transition-opacity before:duration-700 before:opacity-100
            after:absolute after:inset-0 after:rounded-full after:transition-opacity after:duration-700 after:opacity-0
            hover:after:opacity-100 hover:before:opacity-0
            ${fillBase[globalIndex % 2]} ${fillHover[globalIndex % 2]}`}
        >
          <div className="relative z-10 flex flex-col items-center gap-2">
            <img src={service.icon} alt="" aria-hidden className="h-8 w-8 object-contain sm:h-9 sm:w-9 md:h-8 md:w-8 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-15 2xl:w-15" />
            <h1 className="font-gilroy text-[13px] leading-tight font-semibold text-white sm:text-sm md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl">{service.title}</h1>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-10 z-10 flex items-center justify-center gap-1 opacity-0 transition-opacity duration-700 group-hover:opacity-100">
            <span className="font-gilroy-medium text-sm text-white md:text-sm">Read More</span>
            <img src={arrowicon} alt="" aria-hidden className="h-4 w-4 object-contain" />
          </div>
        </button>
      </div>
    )
  }

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden py-12 px-[4%] md:py-16 md:px-[8%]"
    >
      {/* Cursor glow blob */}
      <div
        ref={blobRef}
        className={`pointer-events-none absolute w-[600px] h-[600px] rounded-full
          bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)]
          blur-3xl z-0 transition-opacity duration-300 ease-out
          ${isInside ? 'opacity-50' : 'opacity-0'}`}
      />

      <style>{`
        .circle-hidden-left {
          transform: translateX(-250px) rotate(-270deg) scale(0.6);
          opacity: 0;
          transition: transform 1.1s ease-in, opacity 1.1s ease-in;
        }
        .circle-hidden-right {
          transform: translateX(250px) rotate(270deg) scale(0.6);
          opacity: 0;
          transition: transform 1.1s ease-in, opacity 1.1s ease-in;
        }
        .circle-visible {
          transform: translateX(0) rotate(0deg) scale(1);
          opacity: 1;
          transition: transform 0.85s ease-in, opacity 0.85s ease-in;
        }
      `}</style>

      <div className="relative z-10 mb-4 flex justify-center md:mb-6">
        <TitleHeader
          badgeText="Software we are using"
          titleLines={['Our Frontend Services']}
          wrapperClassName="flex flex-col items-center text-center"
          titleClassName="text-3xl md:text-5xl lg:text-4xl xl:text-4xl 2xl:text-6xl font-rajdhani font-semibold"
          badgeClassName="text-sm md:text-base lg:text-lg xl:text-base 2xl:text-2xl uppercase px-8 py-2 font-rajdhani font-semibold"
        />
      </div>

      {/* MOBILE VIEW — 2 per row, own observers */}
      <div className="relative z-10 mx-auto flex flex-col gap-y-6 sm:hidden">
        {mobileRows.map((row, mobileRowIndex) => {
          const isVisible = visibleMobileRows[mobileRowIndex]
          const hiddenClass = mobileRowIndex % 2 === 0 ? 'circle-hidden-left' : 'circle-hidden-right'

          return (
            <div
              key={mobileRowIndex}
              ref={(el) => { mobileRowRefs.current[mobileRowIndex] = el }}
              className="grid grid-cols-2 justify-items-center gap-x-3 gap-y-4 px-2"
            >
              {row.map((service, colIndex) => {
                const globalIndex = mobileRowIndex * MOBILE_COLS + colIndex
                return renderCard(service, globalIndex, colIndex, isVisible, hiddenClass, row.length)
              })}
            </div>
          )
        })}
      </div>

      {/* SM AND ABOVE — 3 per row (original layout) */}
      <div className="relative z-10 mx-auto md:mx-5 lg:mx-10 xl:mx-30 hidden sm:flex flex-col gap-y-6 sm:gap-y-7 md:gap-y-8 lg:gap-y-9 xl:gap-y-7 2xl:gap-y-10">
        {desktopRows.map((row, rowIndex) => {
          const isVisible = visibleDesktopRows[rowIndex]
          const hiddenClass = rowIndex % 2 === 0 ? 'circle-hidden-left' : 'circle-hidden-right'
          return (
            <div
              key={rowIndex}
              ref={(el) => { desktopRowRefs.current[rowIndex] = el }}
              className="grid grid-cols-3 justify-items-center gap-x-4 sm:gap-x-5 md:gap-x-6 lg:gap-x-4 xl:gap-x-1 2xl:gap-x-25"
            >
              {row.map((service, colIndex) => {
                const globalIndex = rowIndex * COLS + colIndex
                return renderCard(service, globalIndex, colIndex, isVisible, hiddenClass, row.length)
              })}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default FrontendService